import { NewBookingInput, SettingEndUserApp } from "../../../../generated/graphql"
import { TFunction } from "i18next"
import type { CheckoutBlockerCode } from "../../../../services/reportEndUserEvent"

/** Ein Blocker: maschinenlesbarer Code (Kundenakte) plus übersetzter Hinweistext (UI). */
export type DisabledEntry = { code: CheckoutBlockerCode; text: string }

export type StatusData = {
    showAlert: boolean
    noAttendeeDoesFitCourse: boolean
    /** Sonderfall von noAttendeeDoesFitCourse: ALLE Teilnehmer sind bereits im Kurs gebucht. */
    allAttendeesAlreadyBooked: boolean
    bookerHasAttendees: boolean
    bookingData: NewBookingInput
    selfBookerCourse: boolean
    attendeeAgeDoesFitCourse: boolean
    validSepaDebitDataValidIfSelected: boolean
    allReirementGroupsMet: boolean
    optionalInputFields: SettingEndUserApp["optionalInputFields"] | undefined
    selfBookerCourseWithoutBookersBirthday: boolean
    creatingAttendee: boolean
    inputFieldValues: Array<{ [Key: string]: string | boolean }>
    /** Mollie-Methode gewählt → paymentMethodId nicht nötig */
    selectedMollieMethod?: string | null
    /** Guthaben deckt den offenen Betrag vollständig → keine Zahlart nötig */
    fullyCoveredByCredit?: boolean
    /**
     * Wartelistenplatz auf einem Mollie-Pflicht-Kurs → Zahlung erst beim
     * Nachrücken, die Methodenwahl entfällt im Checkout komplett.
     */
    waitlistPaymentDeferred?: boolean
    /** Abweichende Rechnungsadresse gewählt (Schalter im Web Modul an), aber unvollständig. */
    billingAddressIncomplete?: boolean
}

export const getDisabledText = (statusData: StatusData, t: TFunction): Array<string> => getDisabledEntries(statusData, t).map((entry) => entry.text)

/**
 * Liefert je Blocker Code UND Text. `getDisabledText` ist die reine Text-Sicht
 * davon; die Codes gehen als `blockerCodes` in die Kundenakte
 * (services/reportEndUserEvent). Die Zweige und ihre Reihenfolge sind
 * unverändert — jeder Zweig trägt genau einen Code.
 */
export const getDisabledEntries = (statusData: StatusData, t: TFunction): Array<DisabledEntry> => {
    const {
        noAttendeeDoesFitCourse,
        bookerHasAttendees,
        bookingData,
        selfBookerCourse,
        attendeeAgeDoesFitCourse,
        validSepaDebitDataValidIfSelected,
        allReirementGroupsMet,
        optionalInputFields,
        selfBookerCourseWithoutBookersBirthday,
        creatingAttendee,
        inputFieldValues,
    } = statusData
    const disabledText: Array<DisabledEntry> = []

    if (noAttendeeDoesFitCourse === true) {
        // Wenn ALLE Teilnehmer bereits gebucht sind, ist der generische "passt nicht"-Text
        // wenig hilfreich — dann erklären wir den konkreten Grund und den nächsten Schritt.
        if (statusData.allAttendeesAlreadyBooked) {
            return [
                {
                    code: "AllAttendeesAlreadyBooked",
                    text: t(
                        "bookingStepSummary.disabledText.allAttendeesAlreadyBooked",
                        "Alle deine Teilnehmer sind bereits in diesem Kurs gebucht. Lege einen neuen Teilnehmer an, um fortzufahren.",
                    ),
                },
            ]
        }
        return [{ code: "NoAttendeeFitsCourse", text: t("bookingStepSummary.disabledText.noAttendeeDoesFitCourse", "Kein Teilnehmer passt zu diesem Kurs.") }]
    }

    if (bookerHasAttendees === false) {
        if (selfBookerCourse) {
            disabledText.push({ code: "BookerBirthdayMissing", text: t("bookingStepSummary.disabledText.bookerHasNoAttendees.selfBookerCourse", "Bitte tragen Sie Ihr Geburtsdatum ein.") })
        } else {
            disabledText.push({ code: "BookerHasNoAttendees", text: t("bookingStepSummary.disabledText.bookerHasNoAttendees.otherBookerCourse", "Bitte lege zunächst einen Teilnehmer für den Kurs an.") })
        }
        return disabledText
    }

    if (bookingData?.paymentMethodId === undefined && !statusData.selectedMollieMethod && !statusData.fullyCoveredByCredit && !statusData.waitlistPaymentDeferred) {
        disabledText.push({ code: "PaymentMethodMissing", text: t("bookingStepSummary.disabledText.paymentMethodId", "Bitte wähle eine Bezahlmethode.") })
    }

    if (bookingData?.attendeeId === undefined) {
        disabledText.push({ code: "AttendeeMissing", text: t("bookingStepSummary.disabledText.attendeeId", "Bitte wähle einen Teilnehmer.") })
    }

    if (attendeeAgeDoesFitCourse === false) {
        disabledText.push({ code: "AttendeeAgeDoesNotFit", text: t("bookingStepSummary.disabledText.attendeeAgeDoesFitCourse", "Bitte wähle einen Teilnehmer der zu diesem Kurs passt.") })
    }

    if (validSepaDebitDataValidIfSelected === false) {
        disabledText.push({ code: "SepaDataInvalid", text: t("bookingStepSummary.disabledText.validSepaDebitDataValidIfSelected", "Bitte überprüfe deine SEPA-Lastschrift-Daten.") })
    }

    if (creatingAttendee === true) {
        disabledText.push({ code: "AttendeeCreationPending", text: t("bookingStepSummary.disabledText.creatingAttendee", "Bitte bestätige den neu angelegten Teilnehmer.") })
    }

    if (allReirementGroupsMet === false) {
        disabledText.push({ code: "RequirementGroupsUnmet", text: t("bookingStepSummary.disabledText.allReirementGroupsMet", "Bitte bestätige die Teilnahmebedingungen.") })
    }
    // Optional Chaining: bookingData kann beim Direktaufruf der Summary null sein (kein Crash)
    if (Boolean(bookingData?.agbConfirmed) === false && optionalInputFields?.some((field) => field.name === "agb")) {
        disabledText.push({ code: "AgbUnconfirmed", text: t("bookingStepSummary.disabledText.agb", "Bitte bestätige die AGBs.") })
    }
    if (Boolean(bookingData?.privacyStatementConfirmed) === false && optionalInputFields?.some((field) => field.name === "privacyStatement")) {
        disabledText.push({ code: "PrivacyUnconfirmed", text: t("bookingStepSummary.disabledText.privacyStatement", "Bitte bestätige die Datenschutzerklärung.") })
    }
    if (selfBookerCourseWithoutBookersBirthday) {
        disabledText.push({ code: "BookerBirthdayMissing", text: t("bookingStepSummary.disabledText.selfBookerCourseWithoutBookersBirthday", "Bitte trage dein Geburtsdatum ein.") })
    }
    if (optionalInputFields?.some((field) => field.required === true)) {
        optionalInputFields?.forEach((field) => {
            if (field.required && !bookingData?.additionalInfo?.[field.label ?? ""]) {
                if (field.type === "Checkbox") {
                    const isChecked = inputFieldValues.some((value) => value[field.name ?? ""] === true)
                    if (!isChecked) disabledText.push({ code: "RequiredInputMissing", text: t("bookingStepSummary.disabledText.optionalInputFields.checkbox", `Bitte bestätige die Checkbox "${field.label}".`) })
                } else if (field.type === "CheckboxGroup") {
                    const fieldData = field as any
                    const minCheckCount = fieldData.minCheckCount ?? 0
                    if (minCheckCount > 0) {
                        const isValid = inputFieldValues.some((value) => value[field.name ?? ""] === true)
                        if (!isValid) {
                            disabledText.push({ code: "RequiredInputMissing", text: t("bookingStepSummary.disabledText.optionalInputFields.checkboxGroup", `Bitte wähle mindestens ${minCheckCount} Option(en) bei "${field.label}".`) })
                        }
                    }
                } else {
                    disabledText.push({ code: "RequiredInputMissing", text: t("bookingStepSummary.disabledText.optionalInputFields.required", `Bitte fülle das Feld "${field.label}" aus.`) })
                }
            }
        })
    }
    if (statusData.billingAddressIncomplete) {
        disabledText.push({
            code: "RequiredInputMissing",
            text: t("bookingStepSummary.disabledText.billingAddress", "Bitte vervollständige die Rechnungsadresse: Firma oder Nachname, Straße, PLZ und Ort."),
        })
    }
    // Additional check for CheckboxGroup fields with minCheckCount > 0 (they are required even if required flag is not set)
    optionalInputFields?.forEach((field) => {
        if (field.type === "CheckboxGroup") {
            const fieldData = field as any
            const minCheckCount = fieldData.minCheckCount ?? 0
            if (minCheckCount > 0 && !field.required) {
                const isValid = inputFieldValues.some((value) => value[field.name ?? ""] === true)
                if (!isValid) {
                    disabledText.push({ code: "RequiredInputMissing", text: t("bookingStepSummary.disabledText.optionalInputFields.checkboxGroup", `Bitte wähle mindestens ${minCheckCount} Option(en) bei "${field.label}".`) })
                }
            }
        }
        if (field.type === "RadioGroup" && field.enabeld) {
            const isValid = inputFieldValues.some((value) => value[field.name ?? ""] === true)
            if (!isValid) {
                disabledText.push({ code: "RequiredInputMissing", text: t("bookingStepSummary.disabledText.optionalInputFields.radioGroup", `Bitte wähle eine Option bei "${field.label}".`) })
            }
        }
    })

    return disabledText
}

export const isSubmitButtonDisabled = (statusData: StatusData): boolean => {
    const {
        showAlert,
        bookingData,
        attendeeAgeDoesFitCourse,
        validSepaDebitDataValidIfSelected,
        creatingAttendee,
        allReirementGroupsMet,
        optionalInputFields,
        selfBookerCourseWithoutBookersBirthday,
        inputFieldValues,
    } = statusData

    // Check if any CheckboxGroup with minCheckCount > 0 is not valid
    const hasInvalidCheckboxGroup =
        optionalInputFields?.some((field) => {
            if (field.type === "CheckboxGroup") {
                const fieldData = field as any
                const minCheckCount = fieldData.minCheckCount ?? 0
                if (minCheckCount > 0) {
                    const isValid = inputFieldValues.some((value) => value[field.name ?? ""] === true)
                    return !isValid
                }
            }
            return false
        }) ?? false

    // Check if any enabled RadioGroup is not valid (RadioGroup is always required when enabled)
    const hasInvalidRadioGroup =
        optionalInputFields?.some((field) => {
            if (field.type === "RadioGroup" && field.enabeld) {
                const isValid = inputFieldValues.some((value) => value[field.name ?? ""] === true)
                return !isValid
            }
            return false
        }) ?? false

    const { selectedMollieMethod, fullyCoveredByCredit, waitlistPaymentDeferred } = statusData
    const submitButtonDisabled =
        showAlert &&
        ((bookingData?.paymentMethodId === undefined && !selectedMollieMethod && !fullyCoveredByCredit && !waitlistPaymentDeferred) ||
            bookingData?.attendeeId === undefined ||
            attendeeAgeDoesFitCourse === false ||
            validSepaDebitDataValidIfSelected === false ||
            creatingAttendee === true ||
            allReirementGroupsMet === false ||
            (Boolean(bookingData?.agbConfirmed) === false && optionalInputFields?.some((field) => field.name === "agb")) ||
            (Boolean(bookingData?.privacyStatementConfirmed) === false && optionalInputFields?.some((field) => field.name === "privacyStatement")) ||
            selfBookerCourseWithoutBookersBirthday ||
            hasInvalidCheckboxGroup ||
            hasInvalidRadioGroup ||
            statusData.billingAddressIncomplete === true)

    return submitButtonDisabled
}
